import { useLocation } from 'react-router-dom';

import { ReactNode, useMemo } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import { withAuth } from '@/components/auth';
import { TabContentLoader } from '@/components/common/TabContentLoader';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  ActivityContentSkeleton,
  BalancesContentSkeleton,
  GroupHeaderSkeleton,
  MembersContentSkeleton,
  ReportsContentSkeleton,
  SettlementsContentSkeleton,
  SummaryContentSkeleton,
} from '@/components/skeleton';

import { useGroupWithDashboardQuery } from '@/hooks/queries/useGroups';
import { useRouteTransition } from '@/hooks/useRouteTransition';

import { useAuthStore } from '@/stores/authStore';

import { GroupTabs } from './GroupTabs';
import { GroupHeader } from './sections/GroupHeader';

interface GroupPageLayoutProps {
  groupId: string;
  children: ReactNode;
  maxWidth?: 'max-w-2xl' | 'max-w-4xl' | 'max-w-6xl' | 'max-w-7xl';
}

/**
 * Determines which content skeleton to show based on the current route
 */
function getContentSkeletonForRoute(pathname: string): ReactNode {
  // Extract the tab segment from the path
  // Paths are like: /groups/:id (summary), /groups/:id/activity, /groups/:id/balances, etc.
  const segments = pathname.split('/');
  const tabSegment = segments[3] || ''; // After /groups/:id

  switch (tabSegment) {
    case 'activity':
      return <ActivityContentSkeleton />;
    case 'balances':
      return <BalancesContentSkeleton />;
    case 'settlements':
      return <SettlementsContentSkeleton />;
    case 'reports':
      return <ReportsContentSkeleton />;
    case 'members':
      return <MembersContentSkeleton />;
    case '': // Root group page = Summary/Overview tab
    default:
      return <SummaryContentSkeleton />;
  }
}

function GroupPageLayoutComponent({
  groupId,
  children,
  maxWidth = 'max-w-2xl',
}: GroupPageLayoutProps) {
  const user = useAuthStore(state => state.user);
  const location = useLocation();
  const { t } = useTranslation();
  const { isTransitioning, startTransition } = useRouteTransition();

  // Load required namespaces
  const groupsLoaded = useNamespace('groups');
  const currenciesLoaded = useNamespace('currencies');
  const membersLoaded = useNamespace('members');
  const commonLoaded = useNamespace('common');

  const allNamespacesLoaded =
    groupsLoaded && currenciesLoaded && membersLoaded && commonLoaded;

  // Fetch group data for header
  const {
    data: group,
    isLoading: groupLoading,
    error: groupError,
  } = useGroupWithDashboardQuery(
    groupId,
    allNamespacesLoaded && !!user && !!groupId
  );

  const handleTabClick = (href: string) => {
    startTransition(href);
  };

  // Determine the correct content skeleton based on the current route
  const contentSkeleton = useMemo(
    () => getContentSkeletonForRoute(location.pathname),
    [location.pathname]
  );

  if (!allNamespacesLoaded || groupLoading) {
    return (
      <AppLayout>
        <div className={`${maxWidth} mx-auto`}>
          <GroupHeaderSkeleton>{contentSkeleton}</GroupHeaderSkeleton>
        </div>
      </AppLayout>
    );
  }

  if (groupError || !group) {
    return (
      <AppLayout>
        <div className={`${maxWidth} mx-auto p-4`}>
          <div className="text-center text-red-600">
            {t('groups:errors.loadFailed')}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className={`${maxWidth} mx-auto space-y-8`}>
        {/* Header with Action Button - shared across all group pages */}
        <GroupHeader group={group} />

        {/* Tab Navigation - shared across all group pages */}
        <GroupTabs
          groupId={groupId}
          className="-mx-4 sm:-mx-6 lg:-mx-8 px-0 sm:px-6 lg:px-8"
          onTabClick={handleTabClick}
          pendingSettlementsCount={group.dashboard?.pendingSettlementsToConfirm}
        />

        {/* Page-specific content */}
        {isTransitioning ? <TabContentLoader /> : children}
      </div>
    </AppLayout>
  );
}

export const GroupPageLayout = withAuth(GroupPageLayoutComponent);
